import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { REPORT_USER_MUTATION } from "@/api/mutations/reportUser";
import {
  ReportUserMutation,
  ReportUserMutationVariables,
} from "@/__generated__/types";
import { MutationErrorHandler } from "@/util/mutationUtils";

export interface UseReportUserParams {
  onCompleted?: (data: ReportUserMutation) => void;
  onError?: (error: unknown) => void;
}

export interface ReportUserResult {
  success: boolean;
  errors: {
    message: string;
    title: string;
  }[];
}

export interface UseReportUserReturn {
  loading: boolean;
  error: unknown;
}

export const useReportUser = ({
  onCompleted,
  onError,
}: UseReportUserParams = {}): [
  (
    reportedUserId: string,
    reason: ReportUserMutationVariables["reason"],
    image: ReportUserMutationVariables["image"],
  ) => Promise<ReportUserResult>,
  UseReportUserReturn,
] => {
  const [reportUserMutation, { loading, error }] = useMutation<
    ReportUserMutation,
    ReportUserMutationVariables
  >(REPORT_USER_MUTATION, {
    onCompleted,
    onError,
  });

  const reportUser = useCallback(
    async (
      reportedUserId: string,
      reason: ReportUserMutationVariables["reason"],
      image: ReportUserMutationVariables["image"],
    ): Promise<ReportUserResult> => {
      try {
        const result = await reportUserMutation({
          variables: {
            reportedUserId,
            reason,
            image,
          },
        });

        const errorHandler = MutationErrorHandler.fromResult(
          result,
          result.errors,
        );
        return errorHandler.toResult();
      } catch (err) {
        const errorHandler = MutationErrorHandler.fromException(err);
        return errorHandler.toResult();
      }
    },
    [reportUserMutation],
  );

  return [reportUser, { loading, error }];
};
